import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../store/useStore';

const languageFlags: Record<string, string> = {
  tr: '🇹🇷', en: '🇬🇧', de: '🇩🇪', fr: '🇫🇷', es: '🇪🇸', it: '🇮🇹',
  ru: '🇷🇺', ar: '🇸🇦', zh: '🇨🇳', ja: '🇯🇵', ko: '🇰🇷', pt: '🇧🇷',
};

const allLanguages = ['tr', 'en', 'de', 'fr', 'es', 'it', 'ru', 'ar', 'zh', 'ja', 'ko', 'pt'];

const LanguageSelector: React.FC = () => {
  const { i18n, t } = useTranslation();
  const { language, setLanguage } = useStore();
  const [open, setOpen] = useState(false);

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    setLanguage(lang);
    setOpen(false);
  };

  return (
    <>
      {/* Left bottom corner - current language */}
      <div className="fixed bottom-6 left-6 z-50">
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center gap-2 bg-white/90 backdrop-blur-md border border-gray-200 text-gray-700 px-3 py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
        >
          <span className="text-lg">{languageFlags[language]}</span>
          <svg className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {open && (
          <>
            <div className="fixed inset-0" onClick={() => setOpen(false)} />
            <div className="absolute bottom-14 left-0 bg-white rounded-2xl shadow-2xl border border-gray-100 p-2 min-w-[200px] animate-slide-up">
              <p className="text-[10px] text-gray-400 uppercase tracking-wider px-3 py-1 font-semibold">Dil / Language</p>
              <div className="grid grid-cols-2 gap-0.5">
                {allLanguages.map((lang) => (
                  <button
                    key={lang}
                    onClick={() => handleLanguageChange(lang)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                      language === lang
                        ? 'bg-[#E31E24] text-white'
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <span>{languageFlags[lang]}</span>
                    <span>{t(`languages.${lang}`)}</span>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Right bottom corner - quick language flags */}
      <div className="fixed bottom-6 right-24 z-40 hidden lg:flex items-center gap-1">
        {['tr', 'en', 'de', 'fr'].map((lang) => (
          <button
            key={lang}
            onClick={() => handleLanguageChange(lang)}
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm transition-all ${
              language === lang
                ? 'bg-[#E31E24] scale-110 shadow-lg'
                : 'bg-white/80 hover:bg-white hover:scale-110 border border-gray-200'
            }`}
            title={t(`languages.${lang}`)}
          >
            {languageFlags[lang]}
          </button>
        ))}
      </div>
    </>
  );
};

export default LanguageSelector;
